import React, { useState, useCallback } from "react";
import { listarCargosPorMatriculas } from "../../../../services/cargosAdicionalesService";
import "../styles/bonificaciones.css";

const currency = new Intl.NumberFormat("es-EC", { style: "currency", currency: "USD" });

export default function BonificacionDetailPanel({
  bonificacion,
  onClose,
  canUpdate,
  onUpdateEstado,
  onRegistrarDescuento,
  onEliminarDescuento,
  onAbonarAMatricula,
  updatingId,
}) {
  const [mostrarForm, setMostrarForm] = useState(false);
  const [confirmarAbono, setConfirmarAbono] = useState(false);
  const [montoAbono, setMontoAbono] = useState("");
  const [montoCubrir, setMontoCubrir] = useState("");
  const [cargos, setCargos] = useState([]);
  const [loadingCargos, setLoadingCargos] = useState(false);
  const [cargoSeleccionado, setCargoSeleccionado] = useState(null);
  const [observacion, setObservacion] = useState("");

  const abrirForm = useCallback(async () => {
    setMostrarForm(true);
    setCargoSeleccionado(null);
    setObservacion("");
    if (!bonificacion?.matriculaId) {
      setCargos([]);
      return;
    }
    setLoadingCargos(true);
    try {
      const data = await listarCargosPorMatriculas([bonificacion.matriculaId]);
      const rows = Array.isArray(data?.rows) ? data.rows : [];
      setCargos(rows.filter((c) => !c.cubierto_por_bonificacion_id));
    } catch (err) {
      console.error("Error cargando cargos adicionales:", err);
      setCargos([]);
    } finally {
      setLoadingCargos(false);
    }
  }, [bonificacion?.matriculaId]);

  const cerrarForm = () => {
    setMostrarForm(false);
    setCargos([]);
    setCargoSeleccionado(null);
    setObservacion("");
    setMontoCubrir("");
  };

  const handleAbonar = async () => {
    const monto = montoAbono ? parseFloat(montoAbono) : null;
    const ok = await onAbonarAMatricula?.(bonificacion.id, monto);
    if (ok) { setConfirmarAbono(false); setMontoAbono(""); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cargoSeleccionado) return;
    const monto = montoCubrir ? parseFloat(montoCubrir) : null;
    const ok = await onRegistrarDescuento?.(bonificacion.id, cargoSeleccionado.id, observacion, monto);
    if (ok) cerrarForm();
  };

  const handleEliminar = () => onEliminarDescuento?.(bonificacion.id);

  if (!bonificacion) {
    return (
      <div className="management-card bonificacion-detail empty h-100">
        <p className="text-muted mb-1">Selecciona una bonificacion para ver el detalle.</p>
        <small className="text-muted">Tip: filtra por referente, referido o estado.</small>
      </div>
    );
  }

  const isUpdating = updatingId === bonificacion.id;
  const tieneDescuento = !!bonificacion.cargoDescuentoId;
  const montoACobrar = tieneDescuento ? bonificacion.montoNeto : bonificacion.monto;
  const puedeGestionarDescuento =
    canUpdate &&
    (bonificacion.estado === "pendiente" || bonificacion.estado === "no_pagada");

  return (
    <div className="management-card bonificacion-detail h-100 d-flex flex-column">

      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-start mb-3">
        <div>
          <p className="management-eyebrow mb-1">Detalle de bonificacion</p>
          <h6 className="mb-0">Bonificacion #{bonificacion.id}</h6>
          <small className="text-muted">
            Generada: {bonificacion.fechaGeneracion ? new Date(bonificacion.fechaGeneracion).toLocaleString() : "-"}
          </small>
        </div>
        <button type="button" className="btn btn-link text-decoration-none text-muted p-0" onClick={onClose}>
          Cerrar
        </button>
      </div>

      {/* Datos principales */}
      <div className="detail-grid mb-3">
        <div>
          <p className="text-muted mb-1">Referente</p>
          <h6 className="mb-0">{bonificacion.referente || "No asignado"}</h6>
        </div>
        <div>
          <p className="text-muted mb-1">Referido</p>
          <h6 className="mb-0">{bonificacion.referido || "No disponible"}</h6>
        </div>
        <div>
          <p className="text-muted mb-1">Monto bono</p>
          {tieneDescuento ? (
            <>
              <small className="text-muted text-decoration-line-through d-block">{currency.format(bonificacion.monto)}</small>
              <h6 className="mb-0 text-success">{currency.format(bonificacion.montoNeto)}</h6>
            </>
          ) : (
            <h6 className="mb-0">{currency.format(bonificacion.monto || 0)}</h6>
          )}
        </div>
        <div>
          <p className="text-muted mb-1">Estado actual</p>
          <h6 className="mb-0 text-capitalize">{bonificacion.estado || "Sin estado"}</h6>
        </div>
      </div>

      {/* Descuento aplicado */}
      {tieneDescuento && (
        <div className="alert alert-warning py-2 px-3 mb-3 small">
          <div className="d-flex justify-content-between align-items-start gap-2">
            <div>
              <strong>Descuento autorizado</strong>
              <div className="mt-1">
                <span className="text-muted">Cargo vinculado:</span>{" "}
                {bonificacion.cargoDescuentoConcepto || `#${bonificacion.cargoDescuentoId}`}
                {bonificacion.cargoDescuentoMonto != null && (
                  <span className="ms-1">({currency.format(bonificacion.cargoDescuentoMonto)})</span>
                )}
              </div>
              {bonificacion.observacionDescuento && (
                <div className="text-muted mt-1">{bonificacion.observacionDescuento}</div>
              )}
              <div className="mt-1 fw-semibold">
                Descuento: -{currency.format(bonificacion.descuento || 0)}
                <span className="ms-2 text-success">
                  A cobrar: {currency.format(montoACobrar || 0)}
                </span>
              </div>
            </div>
            {puedeGestionarDescuento && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm flex-shrink-0"
                disabled={isUpdating}
                onClick={handleEliminar}
                title="Quitar descuento"
              >
                <i className="bi bi-trash" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Acciones */}
      <div className="mt-auto d-flex flex-column gap-2">
        {canUpdate ? (
          <>
            {(bonificacion.estado === "no_pagada" || bonificacion.estado === "anulada") && !confirmarAbono && (
              <>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  disabled={isUpdating}
                  onClick={() => onUpdateEstado?.(bonificacion, "pagada")}
                >
                  {isUpdating
                    ? <i className="bi bi-arrow-repeat me-2 spin" />
                    : <i className="bi bi-check-circle me-2" />}
                  Cobrar bono
                  <span className="ms-1">({currency.format(montoACobrar || 0)})</span>
                </button>

                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  disabled={isUpdating}
                  onClick={() => setConfirmarAbono(true)}
                >
                  <i className="bi bi-arrow-left-right me-2" />
                  Abonar a matrícula del referido
                </button>
              </>
            )}

            {bonificacion.estado === "pendiente" && !confirmarAbono && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                disabled={isUpdating}
                onClick={() => setConfirmarAbono(true)}
              >
                <i className="bi bi-arrow-left-right me-2" />
                Abonar a matrícula del referido
              </button>
            )}

            {confirmarAbono && (
              <div className="border rounded p-2 bg-light">
                <p className="small fw-semibold mb-2">¿Confirmar abono a matrícula?</p>
                <p className="small text-muted mb-2">
                  El referente <strong>{bonificacion.referente || "—"}</strong> dona parte o la totalidad
                  de su bono como pago directo a la matrícula de{" "}
                  <strong>{bonificacion.referido || "—"}</strong>.
                </p>
                <div className="mb-2">
                  <label className="form-label small mb-1">
                    Monto a abonar (máx. {currency.format(montoACobrar || 0)})
                  </label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    placeholder={`${montoACobrar || 0}`}
                    value={montoAbono}
                    min="0.01"
                    max={montoACobrar || 0}
                    step="0.01"
                    onChange={(e) => setMontoAbono(e.target.value)}
                  />
                  <div className="form-text">Dejar vacío para abonar el total del bono.</div>
                </div>
                <div className="d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    disabled={isUpdating}
                    onClick={handleAbonar}
                  >
                    {isUpdating ? <i className="bi bi-arrow-repeat me-1 spin" /> : null}
                    Confirmar abono
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => { setConfirmarAbono(false); setMontoAbono(""); }}
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}


            {puedeGestionarDescuento && !tieneDescuento && !mostrarForm && (
              <button
                type="button"
                className="btn btn-outline-warning btn-sm"
                onClick={abrirForm}
              >
                <i className="bi bi-scissors me-2" />
                Registrar descuento autorizado
              </button>
            )}

            {puedeGestionarDescuento && !tieneDescuento && mostrarForm && (
              <form onSubmit={handleSubmit} className="border rounded p-2 bg-light">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <p className="small fw-semibold mb-0">Cargo del referido a vincular</p>
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-muted p-0 text-decoration-none"
                      onClick={abrirForm}
                      disabled={loadingCargos}
                      title="Recargar cargos"
                    >
                      <i className={`bi bi-arrow-repeat${loadingCargos ? " spin" : ""}`} />
                    </button>
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-muted p-0 text-decoration-none"
                      onClick={cerrarForm}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>

                {/* Lista de cargos pendientes */}
                {loadingCargos ? (
                  <div className="text-center py-2">
                    <span className="spinner-border spinner-border-sm text-secondary" />
                    <span className="ms-2 small text-muted">Cargando cargos...</span>
                  </div>
                ) : cargos.length === 0 ? (
                  <p className="small text-muted mb-2">
                    No hay cargos adicionales pendientes para la matrícula del referido.
                  </p>
                ) : (
                  <div className="list-group small mb-2" style={{ maxHeight: 180, overflowY: "auto" }}>
                    {cargos.map((c) => {
                      const activo = cargoSeleccionado?.id === c.id;
                      return (
                        <button
                          key={c.id}
                          type="button"
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-start${activo ? " list-group-item-warning" : ""}`}
                          onClick={() => setCargoSeleccionado(activo ? null : c)}
                        >
                          <div>
                            <div className="fw-semibold">{c.concepto_nombre}</div>
                            <div className="small text-muted">
                              {c.estudiante_nombre} · {c.codigo_matricula}
                            </div>
                            {c.observaciones && (
                              <div className="small text-muted">{c.observaciones}</div>
                            )}
                          </div>
                          <span className="fw-semibold text-nowrap ms-2">
                            {currency.format(c.monto)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Monto a cubrir (parcial o total) */}
                {cargoSeleccionado && (
                  <div className="mb-2">
                    <label className="form-label small mb-1">
                      Monto a cubrir (máx. {currency.format(cargoSeleccionado.monto)})
                    </label>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      placeholder={`${cargoSeleccionado.monto} (total)`}
                      value={montoCubrir}
                      min="0.01"
                      max={cargoSeleccionado.monto}
                      step="0.01"
                      onChange={(e) => {
                        setMontoCubrir(e.target.value);
                        const cubierto = Math.min(parseFloat(e.target.value) || cargoSeleccionado.monto, cargoSeleccionado.monto);
                        const bonoNeto = bonificacion.monto - cubierto;
                        const restante = cargoSeleccionado.monto - cubierto;
                        let obs = `Bono ${currency.format(bonificacion.monto)} − ${currency.format(cubierto)} = ${currency.format(bonoNeto)} a cobrar`;
                        if (restante > 0) obs += `. Referido debe ${currency.format(restante)}`;
                        setObservacion(obs);
                      }}
                    />
                    <div className="form-text">Dejar vacío para cubrir el cargo completo.</div>
                  </div>
                )}

                <div className="mb-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Observación (opcional)"
                    value={observacion}
                    onChange={(e) => setObservacion(e.target.value)}
                    maxLength={255}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-warning btn-sm w-100"
                  disabled={isUpdating || !cargoSeleccionado}
                >
                  {isUpdating ? <i className="bi bi-arrow-repeat me-1 spin" /> : null}
                  Aplicar descuento
                </button>
              </form>
            )}
          </>
        ) : (
          <small className="text-muted">No tienes permisos para actualizar bonificaciones.</small>
        )}
      </div>
    </div>
  );
}
